<template>
  <div>
    <v-dialog
        v-model="dialog"
        persistent
        class="station-edit"
        max-width="1000"
        @close="$emit('close')"
    >
      <v-card v-if="loading">
        <v-card-text class="mt-0 pt-3">
          Loading...
          <v-progress-linear
              indeterminate
              color="primary"
              class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
      <v-card v-else>
        <v-card-title>
          {{ stationIdentifier ? 'Edit Station' : 'Create Station'}}
        </v-card-title>
        <v-card-text class="dialog-v-text-scrollable">
          <v-alert
              v-if="errorAlert"
              type="error"
              outlined
              transition="scale-transition"
              class="mt-2"
          >
            {{ errorMessage }}
          </v-alert>
          <v-layout wrap>
            <v-flex xs6>
              <google-geo-point
                  ref="location"
                  :latitude.sync="stationData.latitude"
                  :longitude.sync="stationData.longitude"
                  :styles="{
                    width: '100%',
                    height: '300px',
                  }"
              />
            </v-flex>

            <v-flex
              xs6
              class="pl-5"
            >
              <v-flex xs12>
                <v-text-field
                    v-model="stationData.addressLine1"
                    v-validate="{ required: true}"
                    label="Address Line 1"
                    name="addressLine1"
                    maxlength="255"
                    :error-messages="errors.collect('addressLine1')"
                />
              </v-flex>
              <v-flex xs12>
                <v-text-field
                    v-model="stationData.addressLine2"
                    label="Address Line 2"
                    name="addressLine2"
                    maxlength="255"
                    :error-messages="errors.collect('addressLine2')"
                />
              </v-flex>
              <v-flex xs12>
                <v-text-field
                    v-model="stationData.city"
                    v-validate="{ required: true}"
                    label="City"
                    name="city"
                    maxlength="255"
                    :error-messages="errors.collect('city')"
                />
              </v-flex>
              <v-flex xs12>
                <v-autocomplete
                    v-model="stationData.state"
                    :items="stateItems"
                    label="State"
                    item-text="state"
                    item-value="stateID"
                />
              </v-flex>
              <v-flex xs12>
                <v-text-field
                    v-model="stationData.zipCode"
                    v-validate="{ required: true}"
                    label="Zip Code"
                    name="zipCode"
                    maxlength="10"
                    :error-messages="errors.collect('zipCode')"
                />
              </v-flex>
            </v-flex>

            <v-flex xs5>
              <v-text-field
                  v-model="stationData.stationId"
                  v-validate="{ required: true}"
                  label="Station ID"
                  name="stationId"
                  maxlength="255"
                  :error-messages="errors.collect('stationId')"
              />
            </v-flex>
            <v-flex
                xs6
                offset-1
            >
              <v-text-field
                  v-model="stationData.internalId"
                  label="Internal ID"
                  name="internalId"
                  maxlength="255"
                  :error-messages="errors.collect('internalId')"
              />
            </v-flex>
            <v-flex xs12>
              <v-text-field
                  v-model="stationData.staffing"
                  label="Staffing"
                  name="staffing"
                  type="number"
                  :error-messages="errors.collect('staffing')"
                  @keydown="integerFilter"
              />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn
              color="blue darken-1"
              text
              @click.native="dialog = false"
          >Cancel</v-btn>
          <v-btn
              :loading="saving"
              color="primary"
              text
              @click.native="validateAndSave()"
          >Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import errorAlertMixins from '@/mixins/errorAlertMixins';
import neris from '@/api/neris';
import fetchAllStates from '@/api/states';
import GoogleGeoPoint from '@/components/GoogleGeoPoint';

export default {
  name: 'EditStationPopup',
  components: { GoogleGeoPoint },
  mixins: [errorAlertMixins],
  data() {
    return {
      dialog: false,
      saving: false,
      loading: false,
      stationIdentifier: null,
      states: null,
      stationData: {
        addressLine1: null,
        addressLine2: null,
        city: null,
        state: null,
        zipCode: null,
        stationId: null,
        internalId: null,
        staffing: null,
        latitude: null,
        longitude: null,
      },
      dictionary: {
        attributes: {
          addressLine1: 'Address Line 1',
          addressLine2: 'Address Line 2',
          city: 'City',
          state: 'State',
          zipCode: 'Zip Code',
          stationId: 'Station ID',
          internalId: 'Internal ID',
          staffing: 'Staffing',
        },
      },
    };
  },
  computed: {
    stateItems() {
      return this.states ? this.states.map((item) => ({
        state: `${item.stateID} - ${item.state}`,
        stateID: item.stateID,
      })) : [];
    },
  },
  mounted() {
    this.$validator.localize('en', this.dictionary);
  },
  methods: {
    async show(stationIdentifier = null, stationData = null) {
      try {
        this.loading = true;
        this.hideError();
        this.stationIdentifier = stationIdentifier;
        this.stationData = stationData || {
          addressLine1: null,
          addressLine2: null,
          city: null,
          state: null,
          zipCode: null,
          stationId: null,
          internalId: null,
          staffing: null,
          latitude: null,
          longitude: null,
        };
        this.dialog = true;
        if (!this.states) {
          const states = await fetchAllStates();
          this.states = states.results;
        }
      } finally {
        this.loading = false;
      }
      await this.$nextTick();
      if (this.$refs.location) {
        this.$refs.location.center();
      }
    },
    async validateAndSave() {
      this.hideError();
      await this.$validator.reset();
      if (!await this.$validator.validateAll()) {
        return;
      }
      try {
        this.saving = true;
        if (this.stationIdentifier) {
          await neris.updateStation(this.stationIdentifier, this.stationData);
        } else {
          await neris.createStation(this.stationData);
        }
        this.$emit('saved');
        this.dialog = false;
      } catch (e) {
        this.parseErrorResponse(e.response, true);
      } finally {
        this.saving = false;
      }
    },

    integerFilter($event) {
      const keyCode = ($event.keyCode ? $event.keyCode : $event.which);
      const ignoreKeys = [8, 37, 39];
      if ((keyCode < 48 || keyCode > 57) && !ignoreKeys.includes(keyCode)) {
        $event.preventDefault();
      }
    },
  },
};
</script>
